import { useMemo, useState } from "react";
import { Button, Popover, Tooltip } from "antd";
import SearchInput from "@/components/UI/SearchInput";
import { CloseOutlined } from "@ant-design/icons";
import FilterIcon from "@/assets/icons/filter.svg?react";
import {
  buildRelativeDateFilterOptions,
  createCategoricalFilterValue,
  createNumericFilter,
  createDateFilter,
  isCategoricalFilter,
  formatFilterLabel,
} from "./filtersUtil";
import ActiveFiltersList from "./ActiveFiltersList";
import AvailableFiltersList from "./AvailableFiltersList";
import FilterPanel from "./FilterPanel";
import { GEO_DATA_TYPE, isGeoFilter } from "./geoFilterUtil";
import { normalizedIncludes } from "@/utils/stringUtils";
import type {
  CategoricalFilterMode,
  CategoricalFilterValue,
  ConfirmedFilters,
  DateFilterCondition,
  FilterValue,
  NumericCondition,
  PendingDeletions,
  SelectedFilters,
  TempSelectedFilters,
} from "./filterTypes";

type FilterMenuPopoverProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  activeFilter: string | null;
  setActiveFilter: (filter: string | null) => void;
  tempSelectedFilters: TempSelectedFilters;
  setTempSelectedFilters: (
    filters:
      | TempSelectedFilters
      | ((prev: TempSelectedFilters) => TempSelectedFilters),
  ) => void;
  confirmedFilters: ConfirmedFilters;
  setConfirmedFilters: (
    filters: ConfirmedFilters | ((prev: ConfirmedFilters) => ConfirmedFilters),
  ) => void;
  pendingDeletions: PendingDeletions;
  setPendingDeletions: (
    deletions:
      | PendingDeletions
      | ((prev: PendingDeletions) => PendingDeletions),
  ) => void;
  currentFilters: SelectedFilters;
  onFiltersChange: (filters: SelectedFilters) => void;
  allFiltersData: any;
  visibleColumns?: string[];
  hiddenColumns?: string[];
  notAvailableColumns?: string[];
  columnsTitleCreator?: (column: string) => string;
};

export default function FilterMenuPopover({
  open,
  setOpen,
  activeFilter,
  setActiveFilter,
  tempSelectedFilters,
  setTempSelectedFilters,
  confirmedFilters,
  setConfirmedFilters,
  pendingDeletions,
  setPendingDeletions,
  currentFilters,
  onFiltersChange,
  allFiltersData,
  visibleColumns = [],
  hiddenColumns = [],
  notAvailableColumns = [],
  columnsTitleCreator,
}: FilterMenuPopoverProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Geo fields are excluded here — radius filters are created and edited on
  // the map view (and via their applied-filter chip), not in this popover.
  const filtersKeys = useMemo(
    () =>
      Object.keys(allFiltersData || {}).filter(
        (key) => allFiltersData?.[key]?.data_type !== GEO_DATA_TYPE,
      ),
    [allFiltersData],
  );
  const filterKeyMeta = useMemo(() => {
    return filtersKeys.map((key) => ({
      key,
      displayName: columnsTitleCreator ? columnsTitleCreator(key) : key,
    }));
  }, [filtersKeys, columnsTitleCreator]);

  const filterKeyMap = useMemo(() => {
    return Object.fromEntries(
      filterKeyMeta.map(({ key, displayName }) => [key, displayName]),
    );
  }, [filterKeyMeta]);

  const availableFilterKeys = useMemo(() => {
    return filterKeyMeta
      .filter(({ key, displayName }) => {
        return (
          !notAvailableColumns.includes(key) &&
          normalizedIncludes(displayName, searchTerm)
        );
      })
      .map(({ key }) => key);
  }, [filterKeyMeta, searchTerm, notAvailableColumns]);

  const filteredActiveFilters = useMemo(() => {
    const matchesSearch = (key: string) => {
      const displayName = filterKeyMap[key] ?? key;
      return normalizedIncludes(displayName, searchTerm);
    };

    const mergedFilters: Record<string, any> = { ...currentFilters };

    Object.entries(confirmedFilters).forEach(([key, value]) => {
      if (value && Array.isArray(value) && value.length > 0) {
        mergedFilters[key] = value;
      }
    });

    const activeFilters = Object.entries(mergedFilters).filter(
      ([key, values]) =>
        values &&
        values.length > 0 &&
        !isGeoFilter(values) &&
        !pendingDeletions.has(key) &&
        matchesSearch(key) &&
        !notAvailableColumns.includes(key),
    );

    return activeFilters;
  }, [
    currentFilters,
    confirmedFilters,
    pendingDeletions,
    searchTerm,
    notAvailableColumns,
    filterKeyMap,
  ]);

  const currentFilterOptions = useMemo(() => {
    if (!activeFilter || !allFiltersData[activeFilter]) {
      return [];
    }

    const filter = allFiltersData[activeFilter];

    return filter.values.map((value: FilterValue) => ({
      label: formatFilterLabel(value),
      value: value,
    }));
  }, [activeFilter, allFiltersData]);

  const handleApply = () => {
    setOpen(false);
    const newFilters: SelectedFilters = { ...currentFilters };

    pendingDeletions.forEach((filterKey) => {
      delete newFilters[filterKey];
    });

    Object.entries(confirmedFilters).forEach(([filterKey, confirmedValue]) => {
      if (Array.isArray(confirmedValue)) {
        newFilters[filterKey] = confirmedValue as FilterValue[];
      } else {
        console.warn("Unexpected confirmed filter value type:", confirmedValue);
      }
    });

    onFiltersChange(newFilters);
    setTempSelectedFilters({});
    setConfirmedFilters({});
    setPendingDeletions(new Set());
    setActiveFilter(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setSearchTerm("");
    setOpen(newOpen);

    if (!newOpen) {
      setTempSelectedFilters({});
      setConfirmedFilters({});
      setPendingDeletions(new Set());
      setActiveFilter(null);
    }
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter === activeFilter ? null : filter);

    if (filter !== activeFilter) {
      if (pendingDeletions.has(filter)) {
        setTempSelectedFilters((prev) => ({
          ...prev,
          [filter]: [],
        }));
      } else if (confirmedFilters[filter]) {
        if (isCategoricalFilter(confirmedFilters[filter])) {
          setTempSelectedFilters((prev) => ({
            ...prev,
            [filter]: [...(confirmedFilters[filter] as CategoricalFilterValue)],
          }));
        } else {
          setTempSelectedFilters((prev) => ({
            ...prev,
            [filter]: [...(confirmedFilters[filter] as any[])],
          }));
        }
      } else if (currentFilters[filter]) {
        if (isCategoricalFilter(currentFilters[filter])) {
          setTempSelectedFilters((prev) => ({
            ...prev,
            [filter]: [...(currentFilters[filter] as CategoricalFilterValue)],
          }));
        } else {
          setTempSelectedFilters((prev) => ({
            ...prev,
            [filter]: [...(currentFilters[filter] as any[])],
          }));
        }
      }
    }
  };

  const handleTempSelectionChange = (
    filterKey: string,
    values: FilterValue[],
    mode: CategoricalFilterMode = "include",
  ) => {
    setTempSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: createCategoricalFilterValue(values, mode),
    }));

    setPendingDeletions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(filterKey);
      return newSet;
    });
  };

  const handleApplyNumericFilter = (
    filterKey: string,
    condition: NumericCondition,
    value?: number | { min: number; max: number },
    min?: number,
    max?: number,
  ) => {
    let actualValue: number | undefined;
    let actualMin: number | undefined;
    let actualMax: number | undefined;

    if (
      typeof value === "object" &&
      value !== null &&
      "min" in value &&
      "max" in value
    ) {
      actualMin = value.min;
      actualMax = value.max;
      actualValue = undefined;
    } else {
      actualValue = value as number;
      actualMin = min;
      actualMax = max;
    }

    const numericCondition = createNumericFilter(
      condition,
      actualValue,
      actualMin,
      actualMax,
    );

    setConfirmedFilters((prev) => ({
      ...prev,
      [filterKey]: [numericCondition],
    }));

    setTempSelectedFilters((prev) => {
      const newTemp = { ...prev };
      delete newTemp[filterKey];
      return newTemp;
    });

    setPendingDeletions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(filterKey);
      return newSet;
    });

    setActiveFilter(null);
  };

  const handleApplyDateFilter = (filterKey: string, filterData: any) => {
    let dateCondition: DateFilterCondition | null = null;

    if (filterData.condition === "no_data") {
      dateCondition = createDateFilter("no_data");
    } else if (filterData.condition === "between" && filterData.range) {
      const relativeOptions = buildRelativeDateFilterOptions(
        filterData.mode,
        filterData.relativePreset,
      );

      dateCondition = createDateFilter(
        "between",
        undefined,
        filterData.range[0],
        filterData.range[1],
        relativeOptions,
      );
    } else if (filterData.date) {
      dateCondition = createDateFilter(filterData.condition, filterData.date);
    }

    if (dateCondition) {
      setConfirmedFilters((prev) => ({
        ...prev,
        [filterKey]: [dateCondition],
      }));

      setTempSelectedFilters((prev) => {
        const newTemp = { ...prev };
        delete newTemp[filterKey];
        return newTemp;
      });

      setPendingDeletions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filterKey);
        return newSet;
      });

      setActiveFilter(null);
    }
  };

  const handleApplyFilter = () => {
    if (activeFilter && tempSelectedFilters[activeFilter]) {
      setConfirmedFilters((prev) => ({
        ...prev,
        [activeFilter]: tempSelectedFilters[activeFilter],
      }));

      setTempSelectedFilters((prev) => {
        const newTemp = { ...prev };
        delete newTemp[activeFilter];
        return newTemp;
      });

      setPendingDeletions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(activeFilter);
        return newSet;
      });
    }

    setActiveFilter(null);
  };

  const handleClearFilter = (filterKey: string) => {
    setPendingDeletions((prev) => new Set(prev).add(filterKey));

    setTempSelectedFilters((prev) => {
      const newTemp = { ...prev };
      delete newTemp[filterKey];
      return newTemp;
    });

    setConfirmedFilters((prev) => {
      const newConfirmed = { ...prev };
      delete newConfirmed[filterKey];
      return newConfirmed;
    });

    if (activeFilter === filterKey) {
      setActiveFilter(null);
    }
  };

  const handleResetAll = () => {
    onFiltersChange({});
    setTempSelectedFilters({});
    setConfirmedFilters({});
    setPendingDeletions(new Set());
    setActiveFilter(null);
  };

  const activeFiltersEntries = filteredActiveFilters;

  const handleClose = () => {
    setActiveFilter(null);
    setTempSelectedFilters({});
    setPendingDeletions(new Set());
  };

  const handleClear = () => {
    setTempSelectedFilters({});
    setConfirmedFilters({});
    setPendingDeletions(new Set());
  };

  const FILTER_BOX_HEIGHT = "440px";
  const FILTER_BOX_WIDTH = "650px";

  const currentDataType = activeFilter
    ? allFiltersData[activeFilter]?.data_type
    : null;

  const hasActiveFilters = useMemo(() => {
    return Object.values(currentFilters).some(
      (values) => values && Array.isArray(values) && values.length > 0,
    );
  }, [currentFilters]);

  const getButtonStyle = () => {
    if (open) {
      return {
        color: "#FFF",
        backgroundColor: "#0E98D0",
        borderColor: "#0E98D0",
      };
    }
    if (hasActiveFilters) {
      return {
        color: "#0E98D0",
        backgroundColor: "#ECFAFF",
        borderColor: "#D0DCDF",
      };
    }
    return {
      color: "#064C68",
      backgroundColor: "white",
      borderColor: "#E0E0E0",
    };
  };

  return (
    <Popover
      content={
        <div
          data-testid="table-filters-menu"
          className="flex flex-col relative"
          style={{ height: FILTER_BOX_HEIGHT, width: FILTER_BOX_WIDTH }}
        >
          <div className="flex flex-row flex-1 overflow-hidden">
            {/*Left Panel Section*/}
            <div className="flex flex-col w-[300px] h-full overflow-y-auto hide-scrollbar border-r pr-3 text-[#2B4752]">
              <div className="mb-3">
                <SearchInput
                  placeholder="Search for filters"
                  value={searchTerm}
                  onChange={setSearchTerm}
                  testId="filter-search-input"
                />
              </div>

              <ActiveFiltersList
                activeFiltersEntries={activeFiltersEntries}
                activeFilter={activeFilter}
                availableFilters={allFiltersData}
                onFilterClick={handleFilterClick}
                onClear={handleClearFilter}
                columnsTitleCreator={columnsTitleCreator}
              />

              <AvailableFiltersList
                availableFilterKeys={availableFilterKeys}
                activeFilter={activeFilter}
                availableFilters={allFiltersData}
                onFilterClick={handleFilterClick}
                visibleColumns={visibleColumns}
                hiddenColumns={hiddenColumns}
                columnsTitleCreator={columnsTitleCreator}
              />
            </div>

            {/* Right Panel */}
            <FilterPanel
              activeFilter={activeFilter}
              currentDataType={currentDataType}
              selectedFilters={currentFilters}
              tempSelectedFilters={tempSelectedFilters}
              currentFilterOptions={currentFilterOptions}
              handleClose={handleClose}
              handleClear={handleClear}
              handleApplyNumericFilter={handleApplyNumericFilter as any}
              handleApplyDateFilter={handleApplyDateFilter}
              handleApplyFilter={handleApplyFilter}
              handleTempSelectionChange={handleTempSelectionChange}
              columnsTitleCreator={columnsTitleCreator}
            />
          </div>
          <div className="border-t pt-2 mt-auto flex justify-between">
            <Button
              type="default"
              className="text-[#2B4752] font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleResetAll();
              }}
            >
              Reset All
            </Button>
            <Button
              type="primary"
              onClick={handleApply}
              disabled={activeFiltersEntries?.length === 0}
            >
              Apply
            </Button>
          </div>
        </div>
      }
      title={
        <div className="flex items-center justify-between pb-1 border-b">
          <p className="text-base font-semibold">Filters</p>
          <Button
            type="text"
            onClick={() => {
              setActiveFilter(null);
              setTempSelectedFilters({});
              setConfirmedFilters({});
              setPendingDeletions(new Set());
              setOpen(false);
            }}
            icon={<CloseOutlined className="text-[#00000073]" />}
          />
        </div>
      }
      trigger="click"
      placement="bottomRight"
      arrow={false}
      open={open}
      onOpenChange={handleOpenChange}
      autoAdjustOverflow={false}
      align={{ offset: [0, 10] }}
    >
      <Tooltip title="Create filters on full dataset" mouseEnterDelay={1}>
        <Button
          icon={<FilterIcon width={24} height={24} />}
          style={getButtonStyle()}
          className="h-[38px] w-[38px] border rounded-md flex items-center justify-center"
          data-testid="table-toolbar-filters-button"
          data-popover-open={open ? "true" : "false"}
        />
      </Tooltip>
    </Popover>
  );
}
